import * as Color from "color"
import PptxGenJS, { Slide, ChartOptions, ChartLineDataset } from "pptxgenjs"
import * as React from "react"
import * as ReactDOM from "react-dom"
import CashFlowTable from "./cash-flow-table"
import { ReportConfiguration } from "../../../configuration"
import { getChartBaseOptions, getChartColors } from "../../pptx-configuration/charts"
import { MasterSlide } from "../../pptx-configuration/master-slides"
import {
	getPlaceholderChartBodyOptions,
	Placeholder
} from "../../pptx-configuration/placeholders"
import { getHtmlToPowerPointBaseOptions } from "../../pptx-configuration/slides"
import { DocumentSection } from "../../types"
import { formatTitle } from "../../utils"
import * as CashFlowDomain from "../../../../../domain/sfp/cash-flow-domain"
import * as LoanDomain from "../../../../../domain/sfp/loan-domain"
import * as ResultDomain from "../../../../../domain/sfp/result-domain"
import * as Model from "../../../../../model"
import { ColorsHelper } from "../../../../../util/colors"
import * as Tabledefinition from "./../../../../scenarii/detail/table-definition"
import Operations from "./../../../../scenarii/common/operations"
import * as ScenarioContextUtils from "../../scenario-context"
import { ScenarioContext } from "../../scenario-context"

export function checkAutoFinancingSection(
	configuration: ReportConfiguration
): boolean {
	const autoFinancing = configuration.observation.autoFinancing
	return autoFinancing.tables || autoFinancing.globalCharts
}

export async function createAutoFinancingSection(
	pptx: PptxGenJS,
	configuration: ReportConfiguration,
	numbering: number[],
	localAuthority: Model.LocalAuthority,
	scenario: Model.Scenario,
	ppi: Model.Ppi,
	loanDecompositions: Model.LoanDecomposition[],
	years: number[],
	loans: any,
	propsloan:any,
	scenarioContext: ScenarioContext
): Promise<DocumentSection> {
	var currentYear = new Date().getFullYear()
	// Même scénario que la Synthèse du scénarii de fonctionnement (voir scenario-context)
	const scenarioref = scenarioContext.scenario
	const previousScenario = scenarioContext.previousScenario
	const getOperations = (): Operations => scenarioContext.operations
	const title = formatTitle("Autofinancement brut et net", numbering)
	const slides: Slide[] = []
	const autoFinancing = configuration.observation.autoFinancing
	const reimbursedCapitals = []
	const remainingCapitals = []
	const operationResult = []
	const netCashFlows = []
	const grossCashFlows = []
	const operationExpenses = []
	const operationReceipts = []
	const cafBruteRrf = [];
	const RRF = []
	
	// Emprunts simulés : repris de la dette détaillée, plus lus en base.
	const simulatedTotals = LoanDomain.getSimulatedLoansTotalsByYear(propsloan || [], years)

	years.forEach(y => {
		const totalInterestByYear = LoanDomain.getTotalInterestByYear(propsloan, y)
		// On conditionne sur le mode de la collectivité et non sur l'existence de la ligne
		// en base : une année sans ligne basculerait sur le calcul de la dette détaillée
		// alors que le PPI et l'écran de dette simplifiée y affichent les montants
		// simulés — les chiffres doivent concorder partout.
		if (localAuthority.isDebtSimplified) {
			const simplifiedYear = (localAuthority.localAuthorityDebtSimplifiedYears || []).find(sy => sy.year === y)
			const simulatedYear = LoanDomain.getSimulatedLoanTotals(simulatedTotals, y)
			// Mode dette simplifiée : remboursement repris de l'écran de dette
			// simplifiée, soit emprunts réels + emprunts simulés.
			reimbursedCapitals.push(LoanDomain.getSimplifiedDebtReimbursedCapital(
				localAuthority, ppi, loans, simulatedTotals, y, currentYear))
			remainingCapitals.push(((simplifiedYear && simplifiedYear.amount) || 0) + simulatedYear.remainingCapital)
		} else {
			reimbursedCapitals.push(LoanDomain.getYearReimbursedCapital(loans, y))
			remainingCapitals.push(LoanDomain.getTotalYearRemainingCapital(loanDecompositions, y))
		}
		if(scenarioref){
			// Bascule rétrospectif / prospectif identique à la Synthèse
			const type = ScenarioContextUtils.getOperationType(scenarioContext, y)
			const scenarioYear = ScenarioContextUtils.getScenarioYear(scenarioContext, y)
		const cafBrute = Tabledefinition._aggregateCAFBrute(type, y, localAuthority, getOperations(), previousScenario, [], loans)
		const rrf = Tabledefinition._aggregateRealOperations(type, y, localAuthority, getOperations(),true, previousScenario, [], loans)
		//Résultat de l'année
		operationResult.push(Tabledefinition._reduceResult1(type, y, localAuthority, getOperations(), scenarioref, loans, null, [], totalInterestByYear))
		// CAF nette : reprend le remboursement du capital affiché sur la ligne
		// précédente du tableau, pour que CAF nette = CAF brute - remboursement
		// quel que soit le mode de saisie de la dette.
		netCashFlows.push(CashFlowDomain.calculateNetCashFlow(
			cafBrute, reimbursedCapitals[reimbursedCapitals.length - 1] || 0))
		// CAF brute
		grossCashFlows.push(cafBrute)
		//CAF brute en pourcentage de RRF
		cafBruteRrf.push(CashFlowDomain.getCafBruteforaYear(cafBrute, rrf));
		//Dépenses de fonctionement
		// En dette simplifiée, les intérêts (66111) saisis dans le scénario sont repris, comme dans la Synthèse
		operationExpenses.push(Tabledefinition._aggregateDispenseTotal(type, y, localAuthority, getOperations(), false, previousScenario, [], loans, totalInterestByYear, scenarioYear, localAuthority.isDebtSimplified))
		operationReceipts.push(Tabledefinition._aggregateOperationsTotal(type, y, localAuthority, getOperations(), true, previousScenario, [], loans))
		// RRF
		RRF.push(rrf);
		}else{
			operationResult.push(0)
			netCashFlows.push(0)
			grossCashFlows.push(0)
			operationExpenses.push(0)
			operationReceipts.push(0)
			cafBruteRrf.push(0)
			RRF.push(0)
		}
	})
	const debtRefunds = CashFlowDomain.calculateDebtRefundPercentage(
		grossCashFlows, reimbursedCapitals)
		//Total des recettes
	// const operationReceipts = ResultDomain.calculateOperationReceipts(
	// 	localAuthority, scenario, ppi, loanDecompositions, years)
	const deleveragingYears = CashFlowDomain.calculateCashFlowDeleveragingYears(
		grossCashFlows, remainingCapitals)
	
	// Épargnes calculées compte par compte avec le moteur de la Synthèse
	const amountFinder: CashFlowDomain.AccountAmountFinder = (laca, receipts, year) =>
		ScenarioContextUtils.findAccountValue(scenarioContext, laca, receipts, year)
	const epargneCourante = CashFlowDomain.getEpargneCouranteforYears(localAuthority,localAuthority.accounts,years, scenario, ppi, loanDecompositions, amountFinder);
	const epargneGestion = CashFlowDomain.getEpargneGestionforYears(localAuthority,localAuthority.accounts,years, scenario, ppi, loanDecompositions, amountFinder);
	const  epargneGestionCourante = CashFlowDomain.getEpargneGestionCouranteforYears(localAuthority,localAuthority.accounts,years, scenario, ppi, loanDecompositions, amountFinder);
	
	if (autoFinancing.tables) {
		const tableId = "management-report-retrospective-cash-flow"
		const tempDiv = document.createElement("div")
		document.head.appendChild(tempDiv)

		const table = <CashFlowTable
			currency={configuration.currency}
			debtRefunds={debtRefunds}
			deleveragingYears={deleveragingYears}
			grossCashFlows={grossCashFlows}
			id={tableId}
			locale={configuration.locale}
			netCashFlows={netCashFlows}
			operationExpenses={operationExpenses}
			operationReceipts={operationReceipts}
			operationResult={operationResult}
			reimbursedCapitals={reimbursedCapitals}
			remainingCapitals={remainingCapitals}
			cafBruteRrf={cafBruteRrf}
			epargneCourante={epargneCourante}
			epargneGestion={epargneGestion}
			epargneGestionCourante={epargneGestionCourante}
			roundness={configuration.generalities.roundness}
			enableHideZeroLines={configuration.generalities.enableHideZeroLines}
			tableConfiguration={configuration.table}
			years={years} />

		ReactDOM.render(table, tempDiv, () => {
			const options = getHtmlToPowerPointBaseOptions(title)
			pptx.addSlidesForTable(tableId, options)
			document.head.removeChild(tempDiv)
		})
	}

	if (autoFinancing.globalCharts) {
		//Graphique Autofinancement brut et net
		// Dépenses / recettes réelles de la Synthèse
		const realExpenses = years.map(y => ScenarioContextUtils.getRealExpense(scenarioContext, y, loans))
		const realReceipts = years.map(y => ScenarioContextUtils.getRealReceipts(scenarioContext, y, loans))

		const slide = pptx.addNewSlide(MasterSlide.Chart)
		slide.addText(title, {
			placeholder: Placeholder.Title
		})
		
		slides.push(slide)
		slide.addChart(getChartDataAFBrutNEt(pptx,years,realReceipts,realExpenses,grossCashFlows,netCashFlows,reimbursedCapitals),getChartOptions(" "," "));
		
		//GRAPH CAF EN % DE RRF
		const slideCAF = pptx.addNewSlide(MasterSlide.Chart)
		const titlePourcentCafRRF = formatTitle("Pourcentage d'épargne (CAF / RRF)", numbering)
		slideCAF.addText(titlePourcentCafRRF, {
			placeholder: Placeholder.Title
		})
		slides.push(slideCAF);
		slideCAF.addChart(getChartDataCAF(pptx,years,grossCashFlows,netCashFlows,RRF),getChartOptions("Années","CAF brut/net en % de RRF"));

		//GRAPH CAPACITE DE DESENDETTEMENT		
		const slideDesendettement = pptx.addNewSlide(MasterSlide.Chart)
		const titleTauxDesendettement = formatTitle("Taux de désendettement (CRD / CAF Brute)", numbering)
		slideDesendettement.addText(titleTauxDesendettement, {
			placeholder: Placeholder.Title,
		})
		slides.push(slideDesendettement);
		slideDesendettement.addChart(getChartDataDesendettement(pptx,years,deleveragingYears),getChartOptions("Années","Capacité de désendettemnt (en Années)"));

		//GRAPH EFFETS CISEAUX RECETTES/DEPENSES	
		const slideEffetCiseauxRD = pptx.addNewSlide(MasterSlide.Chart)
		slideEffetCiseauxRD.addText(title, {
			placeholder: Placeholder.Title
		})
		slides.push(slideEffetCiseauxRD);
		slideEffetCiseauxRD.addChart(getChartDataEffetCiseauxRD(pptx,years,realReceipts,realExpenses),getChartOptions("Années","€"));

		//GRAPH EFFETS CISEAUX CAF
		const slideEffetCiseauxCAF = pptx.addNewSlide(MasterSlide.Chart)
		slideEffetCiseauxCAF.addText(title, {
			placeholder: Placeholder.Title
		})
		slides.push(slideEffetCiseauxCAF);
		slideEffetCiseauxCAF.addChart(getChartDataEffetCiseauxCAF(pptx,years,grossCashFlows,netCashFlows,reimbursedCapitals),getChartOptions("Années","€"));
	}

	return { slides, title }
}

function getChartDataCAF(pptx:PptxGenJS,years:number[],grossCashFlows:number[],netCashFlows:number[],RRF:number[]){
	let typeDataChart;
	
	const lineAlerte:number[]=[];
	const lineCritique:number[]=[];
	const lineCible:number[]=[];
	const cafBruteRrf:number[]=[];
	const cafNetteRrf:number[]=[];

	years.forEach((y,i)=>{
		lineAlerte.push(7);
		lineCritique.push(10);
		lineCible.push(15);
		if(RRF[i]>0){
			cafBruteRrf.push(grossCashFlows[i]*100/RRF[i]);
			cafNetteRrf.push(netCashFlows[i]*100/RRF[i]);
		}else{
			cafBruteRrf.push(0);
			cafNetteRrf.push(0);
		}
	});
	
	const labels = years.map(y => y.toString())
	typeDataChart =[
		{
			type: pptx.charts.BAR,
			data: [
				{
					name: "CAF Brute en % de RRF",
					labels: labels,
					values: cafBruteRrf,
				},
				{
					name: "CAF Nette en % de RRF",
					labels: labels,
					values: cafNetteRrf,
				}
			],
			options:{
				chartColors:["56abc4","0f2e61"]
			}
		},
		{
			type: pptx.charts.LINE,
			data: [
				{
					name: "Seuil cible",
					labels: labels,
					values: lineCible,
				},
			],
			options:{
				chartColors:["52BD35"]
			}
		}
		,
		{
			type: pptx.charts.LINE,
			data: [
				{
					name: "Seuil d'alerte",
					labels: labels,
					values: lineCritique,
				},
			],
			options:{
				chartColors:["F3D840"],
			}
		},{
			type: pptx.charts.LINE,
			data: [
				{
					name: "Seuil critique",
					labels: labels,
					values: lineAlerte,
				},
			],
			options:{
				chartColors:["F35640"]
			}
		}
		
	]	
	
	return typeDataChart;
}

function getChartDataDesendettement(pptx:PptxGenJS,years:number[],capaDesendettement:number[]){
	let typeDataChart;
	
	const lineAlerte:number[]=[];
	const lineCritique:number[]=[];

	years.forEach((y,i)=>{
		lineAlerte.push(12);
		lineCritique.push(8);
	});
	
	const labels = years.map(y => y.toString())
	typeDataChart =[
		{
			type: pptx.charts.BAR,
			data: [
				{
					name: "Désendettement (en Années)",
					labels: labels,
					values: capaDesendettement,
				}
			]
			,
			options:{
				chartColors:["56abc4"]
			}
		},
		{
			type: pptx.charts.LINE,
			data: [
				{
					name: "Seuil critique (12 ans)",
					labels: labels,
					values: lineAlerte,
				},
			],
			options:{
				chartColors:["F35640"]
			}
		},
		{
			type: pptx.charts.LINE,
			data: [
				{
					name: "Seuil d'alerte (8 ans)",
					labels: labels,
					values: lineCritique,
				},
			],
			options:{
				chartColors:["F3D840"]
			}
		}
	]	
	
	return typeDataChart;
}

function getChartDataEffetCiseauxRD(pptx:PptxGenJS,years:number[],recettes:number[],depenses:number[]){
	let typeDataChart;	
	
	const labels = years.map(y => y.toString())
	typeDataChart =[		
		{
			type: pptx.charts.LINE,
			data: [
				{
					name: "Recettes réelles",
					labels: labels,
					values: recettes,
				},
			],
			options:{
				chartColors:["4f81bd"],
				dataLabelColor: "4f81bd",
				dataLabelFontFace: "Arial",
				dataLabelFontSize: 11,
				dataLabelPosition:"t",
				showValue: true,
			}
		},
		{
			type: pptx.charts.LINE,
			data: [
				{
					name: "Dépenses réelles",
					labels: labels,
					values: depenses,
				},
			],
			options:{
				chartColors:["c0504d"],
				dataLabelColor: "c0504d",
				dataLabelFontFace: "Arial",
				dataLabelFontSize: 11,
				dataLabelPosition:"t",
				showValue: true,
			}
		}
	]	
	
	return typeDataChart;
}

function getChartDataEffetCiseauxCAF(pptx:PptxGenJS,years:number[],cafBrute:number[],cafNette:Number[],RCE:number[]){
	let typeDataChart;	
	
	const labels = years.map(y => y.toString())
	typeDataChart =[
		{
			type: pptx.charts.LINE,
			data: [
				{
					name: "CAF Brute",
					labels: labels,
					values: cafBrute,
				},
			],
			options:{
				chartColors:["9bbb59"],
				dataLabelColor: "9bbb59",
				dataLabelFontFace: "Arial",
				dataLabelFontSize: 11,
				dataLabelPosition:"t",
				showValue: true,
			}
		},
		{
			type: pptx.charts.LINE,
			data: [
				{
					name: "Remboursement Capital Emprunts",
					labels: labels,
					values: RCE,
				},
			],
			options:{
				chartColors:["8064a2"],
				dataLabelColor: "8064a2",
				dataLabelFontFace: "Arial",
				dataLabelFontSize: 11,
				dataLabelPosition:"t",
				showValue: true,
			}
		},
		{
			type: pptx.charts.LINE,
			data: [
				{
					name: "CAF Nette",
					labels: labels,
					values: cafNette,
				},
			],
			options:{
				chartColors:["4bacc6"],
				dataLabelColor: "4bacc6",
				dataLabelFontFace: "Arial",
				dataLabelFontSize: 11,
				dataLabelPosition:"t",
				showValue: true,
			}
		}
	]	
	
	return typeDataChart;
}

function getChartDataAFBrutNEt(pptx:PptxGenJS,years:number[],recettes:number[],depenses:number[],cafBrute:number[],cafNette:Number[],RCE:number[]){
	let typeDataChart;	
	
	const labels = years.map(y => y.toString())
	typeDataChart =[		
		{
			type: pptx.charts.LINE,
			data: [
				{
					name: "Recettes réelles",
					labels: labels,
					values: recettes,
				},
			],
			options:{
				chartColors:["4f81bd"],
				dataLabelColor: "4f81bd",
				dataLabelFontFace: "Arial",
				dataLabelFontSize: 11,
				dataLabelPosition:"t",
				showValue: true,
			}
		},
		{
			type: pptx.charts.LINE,
			data: [
				{
					name: "Dépenses réelles",
					labels: labels,
					values: depenses,
				},
			],
			options:{
				chartColors:["c0504d"],
				dataLabelColor: "c0504d",
				dataLabelFontFace: "Arial",
				dataLabelFontSize: 11,
				dataLabelPosition:"t",
				showValue: true,
			}
		},
		{
			type: pptx.charts.LINE,
			data: [
				{
					name: "CAF Brute",
					labels: labels,
					values: cafBrute,
				},
			],
			options:{
				chartColors:["9bbb59"],
				dataLabelColor: "9bbb59",
				dataLabelFontFace: "Arial",
				dataLabelFontSize: 11,
				dataLabelPosition:"t",
				showValue: true,
			}
		},
		{
			type: pptx.charts.LINE,
			data: [
				{
					name: "Remboursement Capital Emprunts",
					labels: labels,
					values: RCE,
				},
			],
			options:{
				chartColors:["8064a2"]
			}
		},
		{
			type: pptx.charts.LINE,
			data: [
				{
					name: "CAF Nette",
					labels: labels,
					values: cafNette,
				},
			],
			options:{
				chartColors:["4bacc6"]
			}
		}
	]	
	
	return typeDataChart;
}

function getChartOptions(AbsTitre:string,OrdTitle:string):ChartOptions{
	return {
		x: 0.5,
		y: 1.8,
		w: "90%",
		h: "60%",
		showLegend: true,
		//
		showCatAxisTitle: true,			
		catAxisTitle: AbsTitre,
		catAxisTitleFontSize: 14,
		//
		showValAxisTitle: true,			
		valAxisTitle: OrdTitle,
		valAxisTitleFontSize: 14,
	};
}

	



// WEBPACK FOOTER //
// ./src/component/management-report/generator/observation-generator/autofinancing-generator/index.tsx